const PHOTO_TTL = 5 * 60 * 1000;
const MISSING_TTL = 60 * 1000;
const ERROR_TTL = 10 * 1000;
const MAX_ENTRIES = 40;
const EMPTY_PHOTO = Object.freeze({ source: "", status: "idle" });
const photos = new Map();

export const getUserPhotoKey = (userId, companyId) =>
  userId && companyId ? `${companyId}/${userId}` : "";

const releaseSource = (source) => {
  if (source?.startsWith("blob:")) URL.revokeObjectURL(source);
};

const publish = (entry, snapshot) => {
  entry.snapshot = snapshot;
  entry.listeners.forEach((listener) => listener());
};

const prunePhotos = () => {
  for (const [key, entry] of photos) {
    if (photos.size <= MAX_ENTRIES) break;
    if (!entry.listeners.size && !entry.request) {
      releaseSource(entry.snapshot.source);
      photos.delete(key);
    }
  }
};

const getEntry = (key) => {
  if (!photos.has(key)) {
    photos.set(key, {
      snapshot: EMPTY_PHOTO,
      expiresAt: 0,
      reload: false,
      request: null,
      listeners: new Set(),
    });
  }
  return photos.get(key);
};

export const getUserPhotoSnapshot = (key) =>
  photos.get(key)?.snapshot ?? EMPTY_PHOTO;

export const subscribeUserPhoto = (key, listener) => {
  if (!key) return () => {};
  const entry = getEntry(key);
  entry.listeners.add(listener);
  prunePhotos();
  return () => {
    entry.listeners.delete(listener);
    prunePhotos();
  };
};

// The cache owns downloaded URLs and pending requests so route changes and
// multiple avatars can reuse them. A component unmount must not abort a shared request.
export const loadUserPhoto = (key, loader) => {
  if (!key) return;
  const entry = getEntry(key);
  if (entry.request || entry.expiresAt > Date.now()) return;

  const controller = new AbortController();
  const request = { controller };
  const reload = entry.reload;
  entry.request = request;
  entry.reload = false;
  publish(entry, { ...entry.snapshot, status: "loading" });

  Promise.resolve()
    .then(() =>
      controller.signal.aborted ? "" : loader(controller.signal, reload),
    )
    .then((source) => {
      if (entry.request !== request || controller.signal.aborted) {
        releaseSource(source);
        return;
      }
      const previousSource = entry.snapshot.source;
      entry.expiresAt = Date.now() + (source ? PHOTO_TTL : MISSING_TTL);
      publish(entry, { source, status: source ? "ready" : "missing" });
      if (previousSource !== source) releaseSource(previousSource);
    })
    .catch((error) => {
      if (entry.request !== request || controller.signal.aborted) return;
      const missing = error.status === 404;
      entry.expiresAt = Date.now() + (missing ? MISSING_TTL : ERROR_TTL);
      const source = missing ? "" : entry.snapshot.source;
      const previousSource = entry.snapshot.source;
      publish(entry, { source, status: missing ? "missing" : "error" });
      if (previousSource !== source) releaseSource(previousSource);
    })
    .finally(() => {
      if (entry.request === request) entry.request = null;
      prunePhotos();
    });
};

export const invalidateUserPhoto = (key) => {
  const entry = photos.get(key);
  if (!entry) return;
  entry.request?.controller.abort();
  entry.request = null;
  entry.expiresAt = 0;
  entry.reload = true;
  // Keep the existing photo visible while its replacement downloads.
  publish(entry, { ...entry.snapshot, status: "idle" });
};

export const clearUserPhotoCache = () => {
  for (const [key, entry] of photos) {
    entry.request?.controller.abort();
    entry.request = null;
    entry.expiresAt = 0;
    entry.reload = false;
    releaseSource(entry.snapshot.source);
    publish(entry, EMPTY_PHOTO);
    if (!entry.listeners.size) photos.delete(key);
  }
};
